import type { SensorMapping } from '../../api/controller';

/** A source without data for longer than this is marked as stale (push cycles are 1–24 h). */
export const STALE_AFTER_MS = 26 * 3_600_000;

export function isStale(iso: string | null, now: number = Date.now()): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && now - t > STALE_AFTER_MS;
}

export function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** A measured value with up to `digits` decimals (2 by default) and its unit. */
export function formatValue(value: number, unit: string | null, language: string, digits = 2): string {
  const number = new Intl.NumberFormat(language, { maximumFractionDigits: digits }).format(value);
  return unit ? `${number} ${unit}` : number;
}

/** The mapping's display name: its label, else the key without the domain (sensor.foo → foo). */
export function mappingName(mapping: Pick<SensorMapping, 'label' | 'sourceKey'>): string {
  if (mapping.label) return mapping.label;
  const dot = mapping.sourceKey.indexOf('.');
  return dot > 0 ? mapping.sourceKey.slice(dot + 1) : mapping.sourceKey;
}

/** Price input ("0,32", "0.3245") in currency units → cents per kWh (4 decimals); null when invalid. */
export function parsePricePerKwh(input: string): number | null {
  const raw = input.trim().replace(/\s/g, '').replace(',', '.');
  // Digits with at most one inner decimal point (no nested-quantifier regex: security/detect-unsafe-regex).
  if (!/^[0-9.]+$/.test(raw) || raw.split('.').length > 2 || raw.startsWith('.') || raw.endsWith('.')) return null;
  const units = Number(raw);
  if (!Number.isFinite(units) || units > 1000) return null;
  return Math.round(units * 100 * 10_000) / 10_000;
}

/** Cents per kWh as a price in the currency, with up to 4 decimals (e.g. 0,3245 €). */
export function formatPricePerKwh(cents: number, language: string, currency: string): string {
  return new Intl.NumberFormat(language, { style: 'currency', currency, maximumFractionDigits: 4 }).format(cents / 100);
}

/** The price as the input shows it (currency units, no symbol). */
export function priceInputValue(cents: number | null, language: string): string {
  if (cents === null) return '';
  return new Intl.NumberFormat(language, { maximumFractionDigits: 4, useGrouping: false }).format(cents / 100);
}
